import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import { Save, X } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { createType } from "@/lib/stores/typesStore";
import { generateUUID } from "@/lib/utils/uuid";
import { Title } from "@radix-ui/react-toast";
import { TitleField } from "@/components/ui/text_fields/TitleField";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TypeCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface TypeData {
  name: string;
  element_type: string;
  group: string;
  description: string;
  elements: string;
}

export default function TypeCreator({ isOpen, onClose, onSuccess }: TypeCreatorProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  // Element type options
  const elementTypes = [
    "text",
    "boolean", 
    "number",
    "date",
    "email",
    "url",
    "image",
    "file",
    "select",
    "multiselect",
    "textarea",
    "rich_text",
  ];

  const [typeData, setTypeData] = useState<TypeData>({
    name: "",
    element_type: "text",
    group: "",
    description: "",
    elements: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedGame || !typeData.name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a type name",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Parse elements if provided
      const elementNames = typeData.elements.trim()
        ? typeData.elements
            .split(",")
            .map(element => element.trim())
            .filter(element => element.length > 0)
        : [];

      // Create elements array for the type's data
      const elementsData = elementNames.map(elementName => ({
        id: generateUUID(),
        name: elementName,
        description: undefined,
      }));

      // Create the type with all elements in the data array
      const createdType = await createType(
        {
          game_id: selectedGame.id,
          name: typeData.name.trim(),
          element_type: typeData.element_type,
          group: undefined, // No group needed
          description: typeData.description.trim() || undefined,
          data: elementsData, // Include all elements in the data array
        },
        toast
      );

      const elementsCount = elementNames.length;

      toast({
        title: "Success",
        description: `Type "${typeData.name}" created successfully${elementsCount > 0 ? ` with ${elementsCount} element${elementsCount > 1 ? 's' : ''}` : ''}`,
      });

      // Reset form
      setTypeData({
        name: "",
        element_type: "text",
        group: "",
        description: "",
        elements: "",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error creating type:", error);
      toast({
        title: "Error",
        description: "Failed to create type. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setTypeData({
      name: "",
      element_type: "text",
      group: "",
      description: "",
      elements: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="space-y-3 p-3 border border-gray-200 rounded">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{t('platform.cards.widgets.create_type')}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* Type Name */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Type Name *
          </label>
          <TitleField
            value={typeData.name}
            placeholder={t('platform.cards.widgets.eg_character_weapon_location')}
            required={true}
            width={"full"}
            onChange={(value) => setTypeData({ ...typeData, name: value })}
            disabled={isSubmitting}
          />  
          
        </div>

        {/* Element Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Element Type *
          </label>
          <select
            value={typeData.element_type}
            onChange={(e) => setTypeData({ ...typeData, element_type: e.target.value })}
            disabled={isSubmitting}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {elementTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Elements (Comma Separated) */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Elements
          </label>
          <TitleField
            value={typeData.elements}
            placeholder={t('platform.cards.widgets.name_health_damage_armor')}
            required={true}
            onChange={(value) => setTypeData({ ...typeData, elements: value })}
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter element names separated by commas
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description
          </label>
          <Textarea
            value={typeData.description}
            onChange={(e) => setTypeData({ ...typeData, description: e.target.value })}
            placeholder={t('platform.cards.widgets.optional_description_for_this_type')}
            rows={2}
            disabled={isSubmitting}
          />
        </div>

        {/* Preview */}
        {typeData.name.trim() && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Preview
            </label>
            <div className="p-2 bg-gray-50 rounded border space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{typeData.name}</span>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                  {typeData.element_type}
                </span>
              </div>
              {typeData.description && (
                <p className="text-xs text-gray-600">{typeData.description}</p>
              )}
              {typeData.elements.trim() && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">{t('platform.cards.widgets.elements')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {typeData.elements
                      .split(",")
                      .map(element => element.trim())
                      .filter(element => element.length > 0)
                      .map((element, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-medium"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-600 bg-opacity-60" />
                          {element}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button 
          onClick={handleSubmit} 
          size="sm" 
          className="flex items-center gap-1"
          disabled={isSubmitting || !typeData.name.trim()}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
